import React, { useState, useEffect } from 'react';
import { X, Grid3X3, Palette, Layout, Settings2, DollarSign, Download, Upload, History, RotateCcw, Filter, Blocks, Map, ListTodo, Workflow, Zap, TrendingUp } from 'lucide-react';
import { Project } from '../types';
import { useAuth } from '../context/AuthContext';

interface ProjectSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  gridType: 'lines' | 'dots' | 'none';
  onGridTypeChange: (type: 'lines' | 'dots' | 'none') => void;
  gridColor: string;
  onGridColorChange: (color: string) => void;
  currentLayout: string;
  onLayoutChange: (layout: string) => void;
  currentColor: string;
  onColorChange: (color: string) => void;
  project?: Project | null;
  onUpdateProject?: (updates: Partial<Project>) => void;
  sectors?: string[];
  onAddSector?: (sectorName: string) => void;
  onRemoveSector?: (sectorName: string) => void;
}

const SECTORS = ['Logistica', 'Qualidade', 'Producao', 'Vendas', 'Financeiro', 'Padrão'];

export const ProjectSettingsModal: React.FC<ProjectSettingsModalProps> = ({
  isOpen,
  onClose,
  gridType,
  onGridTypeChange,
  gridColor,
  onGridColorChange,
  currentLayout,
  onLayoutChange,
  currentColor,
  onColorChange,
  project,
  onUpdateProject,
  sectors = SECTORS,
  onAddSector,
  onRemoveSector,
}) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'visual' | 'cards' | 'financial' | 'modules'>('visual');
  const [newSectorInput, setNewSectorInput] = useState('');
  
  // Estados para configurações financeiras
  const [sectorCosts, setSectorCosts] = useState<Record<string, number>>({});
  const [defaultHourlyCost, setDefaultHourlyCost] = useState<number>(50);
  const [implementationCost, setImplementationCost] = useState<number>(0);
  const [financialHistory, setFinancialHistory] = useState<Array<{
    timestamp: string;
    user: string;
    changes: Partial<{
      sectorHourlyCosts: Record<string, number>;
      defaultHourlyCost: number;
      implementationCost: number;
    }>;
  }>>([]);
  const [showHistory, setShowHistory] = useState(false);
  
  // Estados para filtros do histórico
  const [historyFilter, setHistoryFilter] = useState<{
    dateFrom?: string;
    dateTo?: string;
    user?: string;
    changeType?: 'sectorHourlyCosts' | 'defaultHourlyCost' | 'implementationCost' | 'all';
  }>({ changeType: 'all' });
  const [showFilters, setShowFilters] = useState(false);

  // Carregar configurações financeiras do projeto
  useEffect(() => {
    if (project?.financialSettings) {
      setSectorCosts(project.financialSettings.sectorHourlyCosts || {});
      setDefaultHourlyCost(project.financialSettings.defaultHourlyCost || 50);
      setImplementationCost(project.financialSettings.implementationCost || 0);
      setFinancialHistory(project.financialSettings.history || []);
    } else {
      // Valores padrão
      setSectorCosts({});
      setDefaultHourlyCost(50);
      setImplementationCost(0);
      setFinancialHistory([]);
    }
  }, [project]);

  const handleSaveFinancialSettings = () => {
    if (onUpdateProject) {
      // Identificar quais campos foram alterados comparando com valores anteriores
      const previousSettings = project?.financialSettings;
      const changes: Partial<{
        sectorHourlyCosts: Record<string, number>;
        defaultHourlyCost: number;
        implementationCost: number;
      }> = {};
      
      // Verificar mudanças em sectorHourlyCosts
      const prevSectorCosts = previousSettings?.sectorHourlyCosts || {};
      const sectorChanged = JSON.stringify(prevSectorCosts) !== JSON.stringify(sectorCosts);
      if (sectorChanged) {
        changes.sectorHourlyCosts = sectorCosts;
      }
      
      // Verificar mudanças em defaultHourlyCost
      if (previousSettings?.defaultHourlyCost !== defaultHourlyCost) {
        changes.defaultHourlyCost = defaultHourlyCost;
      }
      
      // Verificar mudanças em implementationCost
      if (previousSettings?.implementationCost !== implementationCost) {
        changes.implementationCost = implementationCost;
      }
      
      // Só criar entrada de histórico se houver mudanças
      let newHistory = [...financialHistory];
      if (Object.keys(changes).length > 0) {
        const userName = user?.email || user?.user_metadata?.name || 'Usuário Anônimo';
        const historyEntry = {
          timestamp: new Date().toISOString(),
          user: userName,
          changes,
        };
        newHistory = [...newHistory, historyEntry].slice(-50); // Manter apenas últimas 50 alterações
      }
      
      onUpdateProject({
        financialSettings: {
          sectorHourlyCosts: sectorCosts,
          defaultHourlyCost,
          implementationCost,
          history: newHistory,
        }
      });
    }
  };

  const handleExportFinancialSettings = () => {
    const data = {
      sectorHourlyCosts: sectorCosts,
      defaultHourlyCost,
      implementationCost,
      exportedAt: new Date().toISOString(),
      projectName: project?.nome_projeto || 'Projeto',
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `configuracoes-financeiras-${project?.nome_projeto || 'projeto'}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const validateFinancialSettings = (data: any): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    // Validar estrutura básica
    if (!data || typeof data !== 'object') {
      errors.push('Arquivo inválido: deve ser um objeto JSON válido');
      return { valid: false, errors };
    }
    
    // Validar sectorHourlyCosts (opcional, mas se presente deve ser objeto)
    if (data.sectorHourlyCosts !== undefined) {
      if (typeof data.sectorHourlyCosts !== 'object' || Array.isArray(data.sectorHourlyCosts)) {
        errors.push('sectorHourlyCosts deve ser um objeto');
      } else {
        // Validar valores numéricos dentro de sectorHourlyCosts
        for (const [key, value] of Object.entries(data.sectorHourlyCosts)) {
          if (typeof value !== 'number' || value < 0 || !isFinite(value)) {
            errors.push(`Valor inválido para setor "${key}": deve ser um número positivo`);
          }
        }
      }
    }
    
    // Validar defaultHourlyCost (opcional, mas se presente deve ser número)
    if (data.defaultHourlyCost !== undefined) {
      if (typeof data.defaultHourlyCost !== 'number' || data.defaultHourlyCost < 0 || !isFinite(data.defaultHourlyCost)) {
        errors.push('defaultHourlyCost deve ser um número positivo');
      }
    }
    
    // Validar implementationCost (opcional, mas se presente deve ser número)
    if (data.implementationCost !== undefined) {
      if (typeof data.implementationCost !== 'number' || data.implementationCost < 0 || !isFinite(data.implementationCost)) {
        errors.push('implementationCost deve ser um número positivo');
      }
    }
    
    // Validar valores dentro de limites razoáveis
    if (data.defaultHourlyCost !== undefined && data.defaultHourlyCost > 10000) {
      errors.push('defaultHourlyCost parece muito alto (> R$ 10.000/h). Verifique o valor.');
    }
    
    if (data.implementationCost !== undefined && data.implementationCost > 100000000) {
      errors.push('implementationCost parece muito alto (> R$ 100 milhões). Verifique o valor.');
    }
    
    return { valid: errors.length === 0, errors };
  };

  const handleImportFinancialSettings = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      
      // Validar tamanho do arquivo (máximo 1MB)
      if (file.size > 1024 * 1024) {
        alert('Arquivo muito grande. O tamanho máximo é 1MB.');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = JSON.parse(event.target?.result as string);
          
          // Validar dados
          const validation = validateFinancialSettings(data);
          if (!validation.valid) {
            alert(`Erro na validação:\n${validation.errors.join('\n')}`);
            return;
          }
          
          // Aplicar dados validados
          if (data.sectorHourlyCosts) setSectorCosts(data.sectorHourlyCosts);
          if (data.defaultHourlyCost !== undefined) setDefaultHourlyCost(data.defaultHourlyCost);
          if (data.implementationCost !== undefined) setImplementationCost(data.implementationCost);
          
          alert('Configurações importadas com sucesso!');
        } catch (error) {
          console.error('Erro ao importar configurações:', error);
          alert('Erro ao importar configurações. Verifique se o arquivo está no formato JSON válido.');
        }
      };
      reader.onerror = () => {
        alert('Erro ao ler o arquivo. Tente novamente.');
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const handleRevertToHistoryEntry = (entryIndex: number) => {
    if (!confirm('Tem certeza que deseja reverter para esta versão? As alterações atuais serão perdidas.')) {
      return;
    }
    
    const entry = financialHistory[entryIndex];
    if (!entry) return;
    
    // Aplicar valores do histórico
    if (entry.changes.sectorHourlyCosts) {
      setSectorCosts(entry.changes.sectorHourlyCosts);
    }
    if (entry.changes.defaultHourlyCost !== undefined) {
      setDefaultHourlyCost(entry.changes.defaultHourlyCost);
    }
    if (entry.changes.implementationCost !== undefined) {
      setImplementationCost(entry.changes.implementationCost);
    }
    
    // Criar nova entrada de histórico para a reversão
    const userName = user?.email || user?.user_metadata?.name || 'Usuário Anônimo';
    const revertEntry = {
      timestamp: new Date().toISOString(),
      user: userName,
      changes: {
        sectorHourlyCosts: entry.changes.sectorHourlyCosts,
        defaultHourlyCost: entry.changes.defaultHourlyCost,
        implementationCost: entry.changes.implementationCost,
      }
    };
    
    const newHistory = [...financialHistory, revertEntry].slice(-50);
    setFinancialHistory(newHistory);
    
    if (onUpdateProject) {
      onUpdateProject({
        financialSettings: {
          sectorHourlyCosts: entry.changes.sectorHourlyCosts || sectorCosts,
          defaultHourlyCost: entry.changes.defaultHourlyCost ?? defaultHourlyCost,
          implementationCost: entry.changes.implementationCost ?? implementationCost,
          history: newHistory,
        }
      });
    }
  };

  // Filtrar histórico baseado nos filtros aplicados
  const filteredHistory = financialHistory.filter(entry => {
    if (historyFilter.dateFrom && entry.timestamp < historyFilter.dateFrom) return false;
    if (historyFilter.dateTo && entry.timestamp > historyFilter.dateTo) return false;
    if (historyFilter.user && !entry.user.toLowerCase().includes(historyFilter.user.toLowerCase())) return false;
    if (historyFilter.changeType && historyFilter.changeType !== 'all') {
      if (!entry.changes[historyFilter.changeType]) return false;
    }
    return true;
  });

  if (!isOpen) return null;

  const layoutTypes = [
    { value: 'default', label: 'Ficha', icon: Layout },
    { value: 'postit', label: 'Post-it', icon: Layout },
    { value: 'minimal', label: 'Compacto', icon: Layout },
    { value: 'classic', label: 'VSM', icon: Layout },
    { value: 'neon', label: 'Neon', icon: Layout },
    { value: 'blueprint', label: 'Blueprint', icon: Layout },
    { value: 'origami', label: 'Origami', icon: Layout },
    { value: 'kanban', label: 'Kanban', icon: Layout },
    { value: 'ticket', label: 'Ticket', icon: Layout },
  ];

  const colorOptions = [
    { value: 'emerald', label: 'Esmeralda', color: '#10b981' },
    { value: 'blue', label: 'Azul', color: '#3b82f6' },
    { value: 'purple', label: 'Roxo', color: '#a855f7' },
    { value: 'red', label: 'Vermelho', color: '#ef4444' },
    { value: 'orange', label: 'Laranja', color: '#f97316' },
    { value: 'teal', label: 'Verde-água', color: '#14b8a6' },
  ];

  const gridColors = [
    { value: '#cbd5e1', label: 'Cinza Claro' },
    { value: '#94a3b8', label: 'Cinza Médio' },
    { value: '#64748b', label: 'Cinza Escuro' },
    { value: '#e2e8f0', label: 'Cinza Muito Claro' },
  ];

  return (
    <div className="fixed inset-0 z-[10000] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden border border-gray-200">
        {/* Header - Estilo Ribbon */}
        <div className="bg-slate-50 border-b border-gray-200 p-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-200 rounded transition-colors">
              <Settings2 size={18} className="text-gray-700" />
            </div>
            <div>
              <h2 className="font-bold text-sm text-gray-800">Configurações do Projeto</h2>
              <p className="text-[10px] text-gray-500 uppercase font-medium">Ajustes Visuais e Comportamento</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded transition-colors text-gray-600"
          >
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex bg-gray-50 border-b border-gray-200">
          <button 
            onClick={() => setActiveTab('visual')}
            className={`px-6 py-3 text-[11px] font-medium transition-all ${
              activeTab === 'visual' 
                ? 'bg-white border-t-2 border-t-blue-600 text-gray-800' 
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            Visual
          </button>
          <button 
            onClick={() => setActiveTab('cards')}
            className={`px-6 py-3 text-[11px] font-medium transition-all ${
              activeTab === 'cards' 
                ? 'bg-white border-t-2 border-t-blue-600 text-gray-800' 
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            Cards
          </button>
          <button 
            onClick={() => setActiveTab('financial')}
            className={`px-6 py-3 text-[11px] font-medium transition-all ${
              activeTab === 'financial' 
                ? 'bg-white border-t-2 border-t-blue-600 text-gray-800' 
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            Financeiro
          </button>
          <button 
            onClick={() => setActiveTab('modules')}
            className={`px-6 py-3 text-[11px] font-medium transition-all ${
              activeTab === 'modules' 
                ? 'bg-white border-t-2 border-t-blue-600 text-gray-800' 
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            🧱 Recursos
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'visual' ? (
            <div className="space-y-6">
              {/* Grid Settings */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Grid3X3 size={16} className="text-gray-500" />
                  <h3 className="text-sm font-bold text-gray-800">Grade (Grid)</h3>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">Tipo de Grade</label>
                    <div className="flex gap-2">
                      {(['lines', 'dots', 'none'] as const).map((type) => (
                        <button
                          key={type}
                          onClick={() => onGridTypeChange(type)}
                          className={`px-4 py-2 text-xs font-medium rounded transition-colors ${
                            gridType === type
                              ? 'bg-gray-200 text-gray-900'
                              : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                          }`}
                        >
                          {type === 'lines' ? 'Linhas' : type === 'dots' ? 'Pontos' : 'Nenhum'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {gridType !== 'none' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">Cor da Grade</label>
                      <div className="flex gap-2">
                        {gridColors.map((gc) => (
                          <button
                            key={gc.value}
                            onClick={() => onGridColorChange(gc.value)}
                            className={`w-10 h-10 rounded border-2 transition-all ${
                              gridColor === gc.value
                                ? 'border-gray-900 scale-110'
                                : 'border-gray-300 hover:border-gray-400'
                            }`}
                            style={{ backgroundColor: gc.value }}
                            title={gc.label}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : activeTab === 'cards' ? (
            <div className="space-y-6 max-h-[65vh] overflow-y-auto">
              {/* Card Layout */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Layout size={16} className="text-gray-500" />
                  <h3 className="text-sm font-bold text-gray-800">Layout dos Cards</h3>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {layoutTypes.map((layout) => {
                    const Icon = layout.icon;
                    return (
                      <button
                        key={layout.value}
                        onClick={() => onLayoutChange(layout.value)}
                        className={`p-4 border-2 rounded transition-all text-left ${
                          currentLayout === layout.value
                            ? 'border-blue-600 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Icon size={16} className={currentLayout === layout.value ? 'text-blue-600' : 'text-gray-500'} />
                          <span className={`text-xs font-medium ${currentLayout === layout.value ? 'text-blue-600' : 'text-gray-700'}`}>
                            {layout.label}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Card Colors */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Palette size={16} className="text-gray-500" />
                  <h3 className="text-sm font-bold text-gray-800">Cor dos Cards</h3>
                </div>
                
                <div className="grid grid-cols-3 gap-3">
                  {colorOptions.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => onColorChange(color.value)}
                      className={`p-3 border-2 rounded transition-all ${
                        currentColor === color.value
                          ? 'border-gray-900 scale-105'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div 
                        className="w-full h-8 rounded mb-2"
                        style={{ backgroundColor: color.color }}
                      />
                      <span className={`text-xs font-medium ${currentColor === color.value ? 'text-gray-900' : 'text-gray-600'}`}>
                        {color.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : activeTab === 'financial' ? (
            <div className="max-h-[65vh] overflow-y-auto">
              {/* Botões de Ação */}
              <div className="flex gap-2 mb-4 sticky top-0 bg-white z-10 pb-4">
                <button
                  onClick={handleExportFinancialSettings}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 transition-colors"
                >
                  <Download size={14} />
                  Exportar
                </button>
                <button
                  onClick={handleImportFinancialSettings}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded text-xs font-medium hover:bg-gray-700 transition-colors"
                >
                  <Upload size={14} />
                  Importar
                </button>
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded text-xs font-medium hover:bg-gray-300 transition-colors"
                >
                  <History size={14} />
                  Histórico
                </button>
              </div>

              {/* Layout 2-Coluna */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Coluna 1: Custos Base */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded p-4 border border-blue-200">
                  <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <DollarSign size={16} className="text-blue-600" />
                    Custos Base
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Custo/Hora Padrão (R$/h)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={defaultHourlyCost}
                        onChange={(e) => setDefaultHourlyCost(parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-blue-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        placeholder="50.00"
                      />
                      <p className="text-[10px] text-gray-600 mt-1">
                        ✓ Custo usado quando card não tem custo específico
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Custo Total do Projeto (R$)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={implementationCost}
                        onChange={(e) => setImplementationCost(parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-blue-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        placeholder="0.00"
                      />
                      <p className="text-[10px] text-gray-600 mt-1">
                        ✓ Implementação das melhorias propostas
                      </p>
                    </div>
                  </div>
                </div>

                {/* Coluna 2: Resumo Rápido */}
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded p-4 border border-green-200">
                  <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <Settings2 size={16} className="text-green-600" />
                    Resumo Rápido
                  </h3>
                  <div className="space-y-2">
                    <div className="bg-white rounded p-2">
                      <div className="text-[10px] text-gray-600">Custo/Hora Padrão</div>
                      <div className="text-lg font-bold text-green-700">
                        R$ {defaultHourlyCost.toFixed(2)}/h
                      </div>
                    </div>
                    <div className="bg-white rounded p-2">
                      <div className="text-[10px] text-gray-600">Custo Implementação</div>
                      <div className="text-lg font-bold text-green-700">
                        R$ {implementationCost.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </div>
                    </div>
                    <div className="bg-white rounded p-2">
                      <div className="text-[10px] text-gray-600">Departamentos Configurados</div>
                      <div className="text-lg font-bold text-green-700">{sectors.length}</div>
                    </div>
                    <div className="bg-white rounded p-2">
                      <div className="text-[10px] text-gray-600">Última Alteração</div>
                      <div className="text-sm font-bold text-green-700">
                        {new Date().toLocaleDateString('pt-BR')}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Coluna 3-4: Custos por Departamento - Full Width */}
                <div className="lg:col-span-2 bg-gradient-to-br from-purple-50 to-purple-100 rounded p-4 border border-purple-200">
                  <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <DollarSign size={16} className="text-purple-600" />
                    Custos por Departamento
                  </h3>
                  
                  {/* Input para adicionar novo departamento */}
                  <div className="mb-4 flex gap-2">
                    <input
                      type="text"
                      value={newSectorInput}
                      onChange={(e) => setNewSectorInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && newSectorInput.trim()) {
                          onAddSector?.(newSectorInput.trim());
                          setNewSectorInput('');
                        }
                      }}
                      placeholder="Novo departamento..."
                      className="flex-1 px-3 py-2 border border-purple-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                    />
                    <button
                      onClick={() => {
                        if (newSectorInput.trim()) {
                          onAddSector?.(newSectorInput.trim());
                          setNewSectorInput('');
                        }
                      }}
                      className="px-4 py-2 bg-purple-600 text-white rounded text-xs font-bold hover:bg-purple-700 transition-colors"
                    >
                      + Adicionar
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                    {sectors.map((sector) => (
                      <div key={sector} className="bg-white rounded p-3 border border-purple-200">
                        <label className="text-xs font-bold text-gray-700 block mb-2">
                          {sector}
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={sectorCosts[sector] || ''}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value);
                              setSectorCosts(prev => ({
                                ...prev,
                                [sector]: isNaN(value) ? undefined : value
                              }));
                            }}
                            className="flex-1 px-2 py-1 border border-purple-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-purple-500"
                            placeholder={`R$ ${defaultHourlyCost.toFixed(2)}/h`}
                          />
                          {!['Logistica', 'Qualidade', 'Producao', 'Vendas', 'Financeiro', 'Padrão'].includes(sector) && (
                            <button
                              onClick={() => onRemoveSector?.(sector)}
                              className="p-1 rounded hover:bg-red-100 text-red-600 transition-colors"
                              title="Remover"
                            >
                              <X size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-gray-600 mt-3">
                    💡 Configure custos por departamento. Se não definido, usará custo padrão.
                  </p>
                </div>
              </div>

              {/* Histórico de Alterações - Abaixo */}
              {showHistory && (
                <div className="mt-6 bg-gray-50 border border-gray-200 rounded p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-bold text-gray-800 flex items-center gap-2">
                      <History size={14} />
                      Histórico de Alterações
                    </h4>
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded transition-colors"
                    >
                      <Filter size={12} />
                      Filtros
                    </button>
                  </div>
                  
                  {/* Filtros */}
                  {showFilters && (
                    <div className="bg-white p-3 rounded border border-gray-200 mb-3 space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] text-gray-600 mb-1 block">Data Inicial</label>
                          <input
                            type="date"
                            value={historyFilter.dateFrom || ''}
                            onChange={(e) => setHistoryFilter({ ...historyFilter, dateFrom: e.target.value })}
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-gray-600 mb-1 block">Data Final</label>
                          <input
                            type="date"
                            value={historyFilter.dateTo || ''}
                            onChange={(e) => setHistoryFilter({ ...historyFilter, dateTo: e.target.value })}
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-600 mb-1 block">Usuário</label>
                        <input
                          type="text"
                          placeholder="Filtrar por usuário..."
                          value={historyFilter.user || ''}
                          onChange={(e) => setHistoryFilter({ ...historyFilter, user: e.target.value })}
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-600 mb-1 block">Tipo de Alteração</label>
                        <select
                          value={historyFilter.changeType || 'all'}
                          onChange={(e) => setHistoryFilter({ ...historyFilter, changeType: e.target.value as any })}
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                        >
                          <option value="all">Todas</option>
                          <option value="sectorHourlyCosts">Custos por Departamento</option>
                          <option value="defaultHourlyCost">Custo/Hora Padrão</option>
                          <option value="implementationCost">Custo de Implementação</option>
                        </select>
                      </div>
                      <button
                        onClick={() => setHistoryFilter({ changeType: 'all' })}
                        className="w-full px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                      >
                        Limpar Filtros
                      </button>
                    </div>
                  )}
                  
                  {filteredHistory.length === 0 ? (
                    <div className="text-xs text-gray-500 text-center py-4">
                      {financialHistory.length === 0 
                        ? 'Nenhum histórico disponível'
                        : 'Nenhuma entrada corresponde aos filtros aplicados'}
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {filteredHistory.slice().reverse().map((entry, idx) => {
                        const originalIndex = financialHistory.length - 1 - idx;
                        return (
                          <div key={originalIndex} className="text-xs bg-white p-2 rounded border border-gray-200">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="font-medium text-gray-700">
                                  {new Date(entry.timestamp).toLocaleString('pt-BR')}
                                </div>
                                <div className="text-gray-500 mt-1">
                                  Alterado por: {entry.user}
                                </div>
                                <div className="mt-2 space-y-1">
                                  {entry.changes.sectorHourlyCosts && (
                                    <div className="text-gray-600">
                                      <span className="font-medium">Custos por Departamento:</span> {Object.keys(entry.changes.sectorHourlyCosts).length} departamento(s) configurado(s)
                                    </div>
                                  )}
                                  {entry.changes.defaultHourlyCost !== undefined && (
                                    <div className="text-gray-600">
                                      <span className="font-medium">Custo/Hora Padrão:</span> R$ {entry.changes.defaultHourlyCost.toFixed(2)}/h
                                    </div>
                                  )}
                                  {entry.changes.implementationCost !== undefined && (
                                    <div className="text-gray-600">
                                      <span className="font-medium">Custo de Implementação:</span> R$ {entry.changes.implementationCost.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={() => handleRevertToHistoryEntry(originalIndex)}
                                className="ml-2 p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                title="Reverter para esta versão"
                              >
                                <RotateCcw size={14} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : activeTab === 'modules' ? (
            <div className="space-y-6">
              {/* Módulos do Projeto - Sistema "Lego" */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Blocks size={16} className="text-gray-500" />
                  <h3 className="text-sm font-bold text-gray-800">Recursos do Projeto</h3>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-6">
                  <p className="text-xs text-blue-800">
                    <strong>🧱 Sistema Modular "Project as Lego":</strong> Ative ou desative recursos conforme a necessidade do projeto. 
                    Cada módulo pode ser habilitado/desabilitado independentemente.
                  </p>
                </div>

                <div className="space-y-3">
                  {/* VSM Module */}
                  <div className="flex items-start gap-3 p-4 bg-white border border-gray-200 rounded hover:border-blue-300 transition-colors">
                    <input
                      type="checkbox"
                      checked={project?.enabledModules?.vsm ?? false}
                      onChange={(e) => {
                        if (onUpdateProject) {
                          onUpdateProject({
                            enabledModules: {
                              ...(project?.enabledModules || { vsm: false, actionPlan: false, workflowManagement: false, workflowExecution: false, gainsMonitor: false }),
                              vsm: e.target.checked
                            }
                          });
                        }
                      }}
                      className="mt-1 w-4 h-4 text-blue-600"
                      id="module-vsm"
                    />
                    <label htmlFor="module-vsm" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2 mb-1">
                        <Map size={14} className="text-blue-600" />
                        <span className="text-sm font-semibold text-gray-800">Mapa de Fluxo de Valor (VSM)</span>
                      </div>
                      <p className="text-xs text-gray-600">
                        Editor visual de mapeamento de processos com cards, conexões e métricas Lean.
                      </p>
                    </label>
                  </div>

                  {/* Action Plan Module */}
                  <div className="flex items-start gap-3 p-4 bg-white border border-gray-200 rounded hover:border-green-300 transition-colors">
                    <input
                      type="checkbox"
                      checked={project?.enabledModules?.actionPlan ?? false}
                      onChange={(e) => {
                        if (onUpdateProject) {
                          onUpdateProject({
                            enabledModules: {
                              ...(project?.enabledModules || { vsm: false, actionPlan: false, workflowManagement: false, workflowExecution: false, gainsMonitor: false }),
                              actionPlan: e.target.checked
                            }
                          });
                        }
                      }}
                      className="mt-1 w-4 h-4 text-green-600"
                      id="module-actionplan"
                    />
                    <label htmlFor="module-actionplan" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2 mb-1">
                        <ListTodo size={14} className="text-green-600" />
                        <span className="text-sm font-semibold text-gray-800">Plano de Ação</span>
                      </div>
                      <p className="text-xs text-gray-600">
                        Gestão de ações 5W2H com status, responsáveis, prazos e acompanhamento.
                      </p>
                    </label>
                  </div>

                  {/* Workflow Management Module */}
                  <div className="flex items-start gap-3 p-4 bg-white border border-gray-200 rounded hover:border-purple-300 transition-colors">
                    <input
                      type="checkbox"
                      checked={project?.enabledModules?.workflowManagement ?? false}
                      onChange={(e) => {
                        if (onUpdateProject) {
                          onUpdateProject({
                            enabledModules: {
                              ...(project?.enabledModules || { vsm: false, actionPlan: false, workflowManagement: false, workflowExecution: false, gainsMonitor: false }),
                              workflowManagement: e.target.checked
                            }
                          });
                        }
                      }}
                      className="mt-1 w-4 h-4 text-purple-600"
                      id="module-workflow-mgmt"
                    />
                    <label htmlFor="module-workflow-mgmt" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2 mb-1">
                        <Workflow size={14} className="text-purple-600" />
                        <span className="text-sm font-semibold text-gray-800">Workflow Gestão (Master)</span>
                      </div>
                      <p className="text-xs text-gray-600">
                        Criação e configuração de workflows/checklists padrão para distribuição.
                      </p>
                    </label>
                  </div>

                  {/* Workflow Execution Module */}
                  <div className="flex items-start gap-3 p-4 bg-white border border-gray-200 rounded hover:border-indigo-300 transition-colors">
                    <input
                      type="checkbox"
                      checked={project?.enabledModules?.workflowExecution ?? false}
                      onChange={(e) => {
                        if (onUpdateProject) {
                          onUpdateProject({
                            enabledModules: {
                              ...(project?.enabledModules || { vsm: false, actionPlan: false, workflowManagement: false, workflowExecution: false, gainsMonitor: false }),
                              workflowExecution: e.target.checked
                            }
                          });
                        }
                      }}
                      className="mt-1 w-4 h-4 text-indigo-600"
                      id="module-workflow-exec"
                    />
                    <label htmlFor="module-workflow-exec" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2 mb-1">
                        <Zap size={14} className="text-indigo-600" />
                        <span className="text-sm font-semibold text-gray-800">Workflow Execução (Cliente)</span>
                      </div>
                      <p className="text-xs text-gray-600">
                        Execução de workflows/tarefas com acompanhamento de progresso e status.
                      </p>
                    </label>
                  </div>

                  {/* Gains Monitor Module */}
                  <div className="flex items-start gap-3 p-4 bg-white border border-gray-200 rounded hover:border-amber-300 transition-colors">
                    <input
                      type="checkbox"
                      checked={project?.enabledModules?.gainsMonitor ?? false}
                      onChange={(e) => {
                        if (onUpdateProject) {
                          onUpdateProject({
                            enabledModules: {
                              ...(project?.enabledModules || { vsm: false, actionPlan: false, workflowManagement: false, workflowExecution: false, gainsMonitor: false }),
                              gainsMonitor: e.target.checked
                            }
                          });
                        }
                      }}
                      className="mt-1 w-4 h-4 text-amber-600"
                      id="module-gains"
                    />
                    <label htmlFor="module-gains" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingUp size={14} className="text-amber-600" />
                        <span className="text-sm font-semibold text-gray-800">Monitor de Ganhos</span>
                      </div>
                      <p className="text-xs text-gray-600">
                        Cálculo e visualização de ganhos financeiros (ROI, economia, payback).
                      </p>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer - Estilo Ribbon */}
        <div className="bg-gray-50 border-t border-gray-200 p-4 flex justify-end gap-3">
          {activeTab === 'financial' && (
            <button 
              onClick={handleSaveFinancialSettings}
              className="px-6 py-2 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 transition-colors"
            >
              Salvar Configurações Financeiras
            </button>
          )}
          <button 
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 text-gray-800 rounded text-xs font-medium hover:bg-gray-300 transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};
